import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import { HttpClient, HttpEventType, HttpHeaders, HttpRequest } from '@angular/common/http';
import {Deserialize} from 'cerialize';
import {Tree} from "../models/document/tree";
import {BehaviorSubject, merge, Observable, Subscription} from "rxjs";
import {filter, map, switchMap} from "rxjs/operators";
import {objectToFormData} from "object-to-formdata";
import {LogementService} from './logement.service';
import {Node} from '../models/document/node';
import {DocumentSharing} from "../models/documentSharing";
import {Document} from "../models/document/document";
import {DocumentSharingHistory} from "../models/documentSharingHistory";
import {documentObligatoire} from "../models/document/documentObligatoire";
import {DocumentsToShare} from "../models/document/documentsToShare";

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  baseApiUrl: string = environment.api.baseUrl;

  protected _tree: BehaviorSubject<Tree> = new BehaviorSubject(null);
  public readonly tree: Observable<Tree> = this._tree.asObservable().pipe(filter(tree => tree != null));

  constructor(private http: HttpClient, private logementService: LogementService) {

  }

  load(logementId: number): Observable<Tree> {
    return this.http.get(`${this.baseApiUrl}documents/${logementId}`).pipe(switchMap(data => {
        this._tree.next(Deserialize(data, Tree));
        return this.tree;
      })
    );
  }

  public createOrRenameOrMoveFolder(logementId: number, folder: any): Promise<any> {
    return this.http.post(`${this.baseApiUrl}documents/dossier/${logementId}` + (folder.parentId ? `/${folder.parentId}` : ''), objectToFormData(folder)).toPromise();
  }

  public renameOrMoveDocument(logementId: number, document: any): Promise<any> {
    return this.http.post(`${this.baseApiUrl}documents/document/${logementId}` + (document.parentId ? `/${document.parentId}` : ''), objectToFormData(document)).toPromise();
  }

  public renameDocumentResidenceLogement(logementId: number, document: any): Promise<any> {
    const url = `${this.baseApiUrl}documents/rename-document/${logementId}/${document.id}`;
    return this.http.post(url, objectToFormData(document)).toPromise();
  }

  moveBetweenSpaces(data) {
    return this.http.post(
      `${this.baseApiUrl}documents/action/move-between-spaces`,
      data
    ).toPromise();
  }
  public deleteFolder(folder: any): Promise<any> {
    return this.http.delete(`${this.baseApiUrl}documents/dossier/${folder.id}`).toPromise();
  }

  public deleteDocument(document: any): Promise<any> {
    return this.http.delete(`${this.baseApiUrl}documents/document/${document.id}`).toPromise();
  }

  public deleteDocumentCarnet(document: any): Promise<any> {
    return this.http.delete(`${this.baseApiUrl}documents/document-carnet/${document.id}`).toPromise();
  }

  public uploadFiles(folder: Node, data: FormData): Observable<any> {
    const requests = [];
    const progress = {};
    const logementId = this.logementService.getLogementCourantId();
    let headers = new HttpHeaders();
    headers = headers.set('No-Loader', '');
    headers = headers.set('ngsw-bypass', 'true');

    let url = '';

    //TODO Voir si on peut passer par autre chose que le slug 'autres-documents-logement'
    if (folder.slug === 'autres-documents-logement' || folder.documentObligatoireFolder) {
      url = `${this.baseApiUrl}documents-logement/${logementId}`;
    } else {
      url = `${this.baseApiUrl}documents/${folder.id}`;
    }

    data.forEach((file: File) => {
      const singleData = new FormData();
      singleData.append('documents[]', file);
      if (folder.documentObligatoireFolder) {
        singleData.append('nom_document_obligatoire_slug', folder.slug);
      }
      requests.push(this.http.request(
        new HttpRequest('POST', url, singleData, {
          headers: headers,
          reportProgress: true
        })).pipe(
        map(data => {
            if (data.type === HttpEventType.UploadProgress) {
              progress[file.name] = data;
            }
            return progress;
          }
        )
      ));
    });

    return merge(...requests);
  }

  addRole(documentId: number, role: string, isDocumentCarnet: boolean, logementId: number): Promise<any> {
    let url = `${this.baseApiUrl}documents/document/${documentId}/add-role/${role}`;
    if (isDocumentCarnet) {
      url = `${this.baseApiUrl}documents/document/${documentId}/add-role-document-carnet/${role}/logement/${logementId}`;
    }

    return this.http.get(url).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status == 400) {
          return data.error;
        }
        throw new Error('An unexpected error occurred while data loading : ' + data);
      });
  }

  removeRole(documentId: number, role: string, isDocumentCarnet: boolean, logementId: number): Promise<any> {
    let url = `${this.baseApiUrl}documents/document/${documentId}/remove-role/${role}`;
    if (isDocumentCarnet) {
      url = `${this.baseApiUrl}documents/document/${documentId}/remove-role-document-carnet/${role}/logement/${logementId}`;
    }
    return this.http.get(url).toPromise().then(
      (data) => {
        return data;
      },
      (data) => {
        if (data.status == 400) {
          return data.error;
        }
        throw new Error('An unexpected error occurred while data loading : ' + data);
      });
  }

  download(id: number,token: string) {
    window.open(`${this.baseApiUrl}documents/document/download/${id}/${token}`);
  }

  public getDocumentLogementCourantSharing(): Promise<any> {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.get(`${this.baseApiUrl}documents/partage-document/${logementId}`).toPromise().then(
      (data) => {
        return  Deserialize(data, DocumentsToShare);
      },
      (data) => {
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public getDocumentObligatoire(): Promise<any> {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.get(`${this.baseApiUrl}documents-obligatoires/${logementId}`)
      .toPromise()
      .then((data) => {
        return Deserialize(data, documentObligatoire);
      });
  }

  public setDocumentObligatoire(documentId, documentObligatoireId): Promise<any> {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.get(`${this.baseApiUrl}documents/set-document-obligatoire/${logementId}/${documentId}/${documentObligatoireId}`)
      .toPromise()
      .then((data) => {
        return Deserialize(data, documentObligatoire);
      });
  }

  public saveDocumentsSharing(documentSharing) {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.post(`${this.baseApiUrl}documents/partage-document/${logementId}`, documentSharing)
      .toPromise()
      .then((data) => {
        return data;
      }, (data) => {
        if (data.status === 400) {
          return data.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
      });
  }

  public addDocumentToParcoursTravaux(data: FormData, step: number): Observable<any> {
      const requests = [];
      const progress = {};
      const logementId = this.logementService.getLogementCourantId();
      let headers = new HttpHeaders();
      headers = headers.set('No-Loader', '');
      headers = headers.set('ngsw-bypass', 'true');

      const url =  `${this.baseApiUrl}documents/parcours-travaux/${logementId}/${step}`;

    data.forEach((file: File) => {
      const singleData = new FormData();
      singleData.append('documents[]', file);

      requests.push(this.http.request(
        new HttpRequest('POST', url, singleData, {
          headers: headers,
          reportProgress: true
        })).pipe(
        map(data => {
            if (data.type === HttpEventType.UploadProgress) {
              progress[file.name] = data;
            }
            return progress;
          }
        )
      ));
    });

    return merge(...requests);
  }

  public loadDocumentToParcoursTravaux(logementId: number, step: number): Promise<Document[] | void> {
      return this.http.get(`${this.baseApiUrl}documents/parcours-travaux/${logementId}/${step}`).toPromise().then(
        (data) => {
          return Deserialize(data, Document);
        },
        (data) => {
          throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(data)}`);
        }
      );
    }

  public loadDevisDocuments(logementId: number): Promise<Document[] | void> {
    return this.http.get(`${this.baseApiUrl}documents/devis/${logementId}`)
      .toPromise()
      .then(
        (data) => {
          return Deserialize(data, Document);
        },
        (err) => {
          throw new Error(`Error loading devis documents : ${JSON.stringify(err)}`);
        }
      );
  }

  public addDevisDocument(logementId: number, data: FormData): Promise<any> {
    const url = `${this.baseApiUrl}documents/devis/${logementId}`;
    return this.http.post(url, data).toPromise();
  }

  public updateDevisDocument(documentDevisId: number, data: FormData): Promise<any> {
    const url = `${this.baseApiUrl}documents/devis/update-devis/${documentDevisId}`;
    return this.http.post(url, data).toPromise();
  }

  sendMailInformationDecision(logementId: number): Promise<any> {
    const url = `${this.baseApiUrl}documents/devis/mail-information-decision/${logementId}`;
    return this.http.post(url, {}).toPromise();
  }

  public historyDocumentSharing(): Promise<any> {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.get(`${this.baseApiUrl}documents/historique-partage/${logementId}`).toPromise();
  }

  public updateDocumentSharings(data) {
    const logementId = this.logementService.getLogementCourantId();

    return this.http.put(`${this.baseApiUrl}documents/partage-document/${logementId}`, data)
      .toPromise()
      .then((response) => {
        return response;
      }, (response) => {
        if (response.status === 400) {
          return response.error;
        }
        throw new Error(`An unexpected error occurred while data loading ${JSON.stringify(response)}`);
      });
  }

  public deleteDocumentSharing(documentSharing: DocumentSharingHistory): Promise<any> {
    return this.http.delete(`${this.baseApiUrl}documents/partage-document/${documentSharing.id}`).toPromise();
  }
}
